import { DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Button, Dropdown, Flex, MenuProps, Skeleton, Space } from "antd"
import { useContext, useState } from "react"
import {
  useSortWorkareaGroupsMutation,
  useSortWorkareasMutation,
  useWorkareaGroupsQuery,
} from "../../../../domain/workareas/groupsQueries"
import VSpace from "../../../common/VSpace"
import {
  handleDrop,
  isWorkarea,
  moveWorkareaDown,
  moveWorkareaGroupDown,
  moveWorkareaGroupUp,
  moveWorkareaTo,
  moveWorkareaUp,
  SortableItem,
} from "./dndModel"
import PromoteWorkareaDroppable from "./PromoteWorkarea"
import { IndentedWorkareaComponent } from "./WorkareaComponent"
import { SortableWorkareaGroupComponent, WorkareaGroupComponent } from "./WorkareaGroupComponent"
import {
  buildGroupedWorkareas,
  updateWorkareaGroupSortIndex,
  updateWorkareaSortIndex
} from "./workareaGroupsListModel"
import CreateWorkareaGroupModal from "./CreateWorkareaGroupModal"
import { ItemsContext } from "../../../Items/ItemsRoot"
import CreateWorkareaModal from "./CreateWorkareaModal"
import MoveWorkareaModal from "./MoveWorkareaModal"
import { defaultWorkarea, Workarea } from "../../../../domain/job"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-solid-svg-icons"
import JobPlanContext from "../JobPlanContext"

export const WorkareaGroupsList = () => {
  const { jobData: { jobId } } = useContext(ItemsContext)
  const { selectGroup, moveWorkareaOrGroup: handleMoveWorkareaOrGroup, groupedWorkareaData } = useContext(JobPlanContext)

  const [activeItem, setActiveItem] = useState<SortableItem | undefined>()
  const [overItem, setOverItem] = useState<SortableItem | undefined>()
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false)
  const [isCreateWorkareaModalOpen, setIsCreateWorkareaModalOpen] = useState(false)
  const [groupId, setGroupId] = useState<string | undefined>()
  const [movingWorkarea, setMovingWorkarea] = useState<Workarea | undefined>()

  const workareaGroupsQuery = useWorkareaGroupsQuery(jobId)
  const sortWorkareaGroupsMutation = useSortWorkareaGroupsMutation()
  const sortWorkareasMutation = useSortWorkareasMutation()

  const workareaGroups = groupedWorkareaData.workareaGroups
  const workareas = groupedWorkareaData.workareas

  const handleDragStart = (event: DragStartEvent) => {
    setActiveItem(event.active.data.current as SortableItem)
  }

  const handleDragOver = ({ over }: DragOverEvent) => {
    setOverItem(over?.data.current as SortableItem)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveItem(undefined)

    if (!over) {
      return
    }

    const result = handleDrop(
      active.data.current as SortableItem,
      over?.data.current as SortableItem,
      groupedWorkareaData
    )
    return handleMoveWorkareaOrGroup(result)
  }

  const handleMoveGroup = (groupId: string, move: typeof moveWorkareaGroupUp) => {
    const sorting = move(groupedWorkareaData, groupId)
    const optimisticUpdate = updateWorkareaGroupSortIndex(groupedWorkareaData.workareaGroups, sorting)
    sortWorkareaGroupsMutation.mutate({ jobId, groupIds: sorting, optimisticUpdate })
  }

  const handleMoveGroupUp = (groupId: string) => {
    handleMoveGroup(groupId, moveWorkareaGroupUp)
  }

  const handleMoveGroupDown = (groupId: string) => {
    handleMoveGroup(groupId, moveWorkareaGroupDown)
  }

  const handleMoveWorkarea = (groupId: string, workareaId: number, move: typeof moveWorkareaUp) => {
    const sorting = move(buildGroupedWorkareas(groupedWorkareaData.workareaGroups), groupId, workareaId)
    const optimisticUpdate = updateWorkareaSortIndex(groupedWorkareaData.workareaGroups, sorting)
    sortWorkareasMutation.mutate({ jobId, workareaIds: sorting, optimisticUpdate })
  }

  const handleMoveWorkareaUp = (groupId: string, workareaId: number) => {
    handleMoveWorkarea(groupId, workareaId, moveWorkareaUp)
  }

  const handleMoveWorkareaDown = (groupId: string, workareaId: number) => {
    handleMoveWorkarea(groupId, workareaId, moveWorkareaDown)
  }

  const handleMoveWorkareaTo = (groupId: string, workareaId: number) => {
    const workarea = groupedWorkareaData.workareas[groupId].find(x => x.id === workareaId)
    setMovingWorkarea(workarea)
  }

  const handleRemoveWorkareaFromGroup = (groupId: string, workareaId: number) => {
    const workarea = groupedWorkareaData.workareas[groupId].find(x => x.id === workareaId)
    if (!workarea) {
      return
    }

    handleMoveWorkareaOrGroup(moveWorkareaTo(groupedWorkareaData, workarea, 'move-out'))
  }

  const handleWorkareaMovedTo = (groupId: string) => {
    if (!movingWorkarea) {
      return
    }

    handleMoveWorkareaOrGroup(moveWorkareaTo(groupedWorkareaData, movingWorkarea, groupId))
    setMovingWorkarea(undefined)
  }

  if (workareaGroupsQuery.isLoading) {
    return <Skeleton active paragraph={{ rows: 10 }} />
  }

  const items: MenuProps['items'] = [
    {
      label: 'Add Group',
      key: '1',
      onClick: () => setIsCreateGroupModalOpen(true)
    },
    {
      label: 'Add Work Area',
      key: '2',
      onClick: () => setIsCreateWorkareaModalOpen(true)
    },
  ];

  const menuProps = {
    items,
  };

  return <div style={{ fontFamily: 'sans-serif', fontSize: 14 }}>
    <VSpace size="large">
      <DndContext
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={workareaGroups} strategy={verticalListSortingStrategy}>
          {workareaGroups.map(g => <SortableWorkareaGroupComponent
            workareaGroup={g}
            workareas={workareas[g.id]}
            key={g.id}
            onEdit={() => selectGroup(g.id)}
            groupedWorkareas={groupedWorkareaData.workareas}
            activeItem={activeItem}
            overItem={overItem}
            onAddWorkarea={() => {
              setGroupId(g.id)
              setIsCreateWorkareaModalOpen(true)
            }}
            onMoveUp={() => handleMoveGroupUp(g.id)}
            onMoveDown={() => handleMoveGroupDown(g.id)}
            onMoveWorkareaUp={workareaId => handleMoveWorkareaUp(g.id, workareaId)}
            onMoveWorkareaDown={workareaId => handleMoveWorkareaDown(g.id, workareaId)}
            onMoveWorkareaTo={workareaId => handleMoveWorkareaTo(g.id, workareaId)}
            onRemoveWorkareaFromGroup={workareaId => handleRemoveWorkareaFromGroup(g.id, workareaId)}
          />)}
          {activeItem?.type === 'workarea' && <PromoteWorkareaDroppable />}
        </SortableContext>
        <DragOverlay style={{ cursor: 'grabbing' }} dropAnimation={null}>
          {activeItem && (
            isWorkarea(activeItem)
              ? <IndentedWorkareaComponent
                workarea={activeItem}
                workareaGroup={workareaGroups.find(g => g.id === activeItem.workareaGroupId)!}
                onMoveUp={() => { }}
                onMoveDown={() => { }}
                onMoveTo={() => { }}
                onRemoveFromGroup={() => { }}
              />
              : <WorkareaGroupComponent
                workareaGroup={activeItem}
                workareas={workareas[activeItem.id]}
                onEdit={() => { }}
                onMoveUp={() => { }}
                onMoveDown={() => { }}
                onMoveWorkareaUp={() => { }}
                onMoveWorkareaDown={() => { }}
                onMoveWorkareaTo={() => { }}
                onRemoveWorkareaFromGroup={() => { }}
              />
          )}
        </DragOverlay>
      </DndContext>
      <Flex justify="flex-end" style={{marginBottom: '35px'}}>
        <Dropdown menu={menuProps} placement="topLeft">
          <Button>
            <Space>
              <FontAwesomeIcon icon={faPlus} />
              Add
            </Space>
          </Button>
        </Dropdown>
      </Flex>
    </VSpace>
    <CreateWorkareaGroupModal
      isOpen={isCreateGroupModalOpen}
      onClose={() => setIsCreateGroupModalOpen(false)}
      jobId={jobId}
    />
    <CreateWorkareaModal
      jobId={jobId}
      groupId={groupId}
      isOpen={isCreateWorkareaModalOpen}
      onCancel={() => {
        setIsCreateWorkareaModalOpen(false)
        setGroupId(undefined)
      }}
    />
    <MoveWorkareaModal
      visible={!!movingWorkarea}
      workarea={movingWorkarea ?? defaultWorkarea()}
      workareaGroups={groupedWorkareaData.workareaGroups}
      onCancel={() => setMovingWorkarea(undefined)}
      onSubmit={handleWorkareaMovedTo}
    />
  </div>
}

export default WorkareaGroupsList
